import { Component, Object3D } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import { TriggerTarget } from '../interactions/triggerTarget.js';
import {
    Coroutine,
    CoroutineManager,
    waitForSeconds,
    wlUtils,
} from '@sorskoot/wonderland-components';
import { SoundManager, Sounds } from '../core/music-manager.js';

/**
 * Component to toggle the active state of a target object when the trigger is activated.
 * The target object will be activated or deactivated based on the `activateObject` property.
 */
export class JumpScare extends Component {
    static TypeName = 'jump-scare';
    private _triggerTarget?: TriggerTarget | null;

    @property.object()
    targetObject!: Object3D;

    @property.vector3(0, 0, 0)
    targetPosition = [0, 0, 0];

    @property.vector4(0, 0, 0, 0)
    targetRotation = [0, 0, 0, 0];

    @property.float(1.0)
    scareDuration = 1.0;

    @property.enum(
        Object.keys(Sounds).filter((e) => isNaN(Number(e))),
        Sounds.None
    )
    sound: Sounds = Sounds.None;

    private _orginalPosition = new Float32Array(3);
    private _orginalRotation = new Float32Array(4);
    private _done = false;

    private _coroutineManager = new CoroutineManager();

    start(): void {
        this._triggerTarget = this.object.getComponent(TriggerTarget)!;
        // Ensure the target object is active based on the initial state
        this.targetObject.getPositionWorld(this._orginalPosition);
        this.targetObject.getRotationWorld(this._orginalRotation);
    }

    onActivate(): void {
        this._triggerTarget?.onExecute.add(this._onExecute);
    }

    onDeactivate(): void {
        this._triggerTarget?.onExecute.remove(this._onExecute);
    }

    update(dt: number): void {
        this._coroutineManager.update(dt);
    }

    private _onExecute = () => {
        if (this._done) return; // Prevent multiple activations
        this._done = true; // Mark as done to prevent further activations
        this._coroutineManager.addCoroutine(
            new Coroutine(this._scareCoroutine())
        );
    };

    private *_scareCoroutine() {
        if (this.targetObject) {
            this.targetObject.setPositionWorld(this.targetPosition);
            this.targetObject.setRotationWorld(this.targetRotation);
        }
        if (this.sound !== Sounds.None) {
            SoundManager.instance.play(this.sound);
        }
        yield* waitForSeconds(this.scareDuration);
        if (this.targetObject) {
            // Restore the original position and rotation after the scare is done
            this.targetObject.setPositionWorld(this._orginalPosition);
            this.targetObject.setRotationWorld(this._orginalRotation);
        }
    }
}
